import React, { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { ROLES, can } from '../lib/roles'
import { T, card, cardTitle, td } from '../lib/theme'

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (v) => {
  if (!v) return '—'
  return new Date(v).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' })
}
const fmtDT = (v) => {
  if (!v) return '—'
  return new Date(v).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })
}
const fmtNaira = (n) => n ? '₦' + Number(n).toLocaleString(undefined, { maximumFractionDigits:0 }) : '—'

const STATUS_META = {
  reported:        { label:'Reported',        color:'#8b949e', bg:'rgba(139,148,158,.12)', border:'rgba(139,148,158,.3)' },
  under_review:    { label:'Under Review',    color:'#E8A020', bg:'rgba(232,160,32,.15)',  border:'rgba(232,160,32,.3)'  },
  pending_admin:   { label:'Pending Approval',color:'#1E90FF', bg:'rgba(30,144,255,.12)',  border:'rgba(30,144,255,.3)'  },
  pending_payment: { label:'Pending Payment', color:'#f0883e', bg:'rgba(240,136,62,.12)',  border:'rgba(240,136,62,.3)'  },
  closed:          { label:'Closed',          color:'#3fb950', bg:'rgba(63,185,80,.12)',   border:'rgba(63,185,80,.3)'   },
}

const RES_META = {
  warning:     { label:'Warning',     color:'#E8A020' },
  penalty:     { label:'Penalty',     color:'#f0883e' },
  suspension:  { label:'Suspension',  color:'#f85149' },
  termination: { label:'Termination', color:'#ff0000' },
}

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.reported
  return (
    <span style={{ display:'inline-block', padding:'2px 9px', borderRadius:20, fontSize:10,
      fontWeight:700, color:m.color, background:m.bg, border:`1px solid ${m.border}` }}>
      {m.label}
    </span>
  )
}

function ResBadge({ type }) {
  if (!type) return <span style={{ color:T.txtMuted }}>—</span>
  const m = RES_META[type] || {}
  return (
    <span style={{ display:'inline-block', padding:'2px 9px', borderRadius:20, fontSize:10,
      fontWeight:700, color:m.color || T.txtPrimary,
      background:`${m.color}18`, border:`1px solid ${m.color}40` }}>
      {m.label || type}
    </span>
  )
}

const inp = {
  width:'100%', padding:'9px 12px', background:T.surface3, border:`1px solid ${T.border}`,
  borderRadius:6, fontSize:12, fontFamily:T.font, color:T.txtPrimary, outline:'none',
  boxSizing:'border-box',
}
const fieldLabel = {
  fontSize:10, fontWeight:600, color:T.txtMuted, display:'block',
  letterSpacing:'.06em', textTransform:'uppercase', marginBottom:4, marginTop:12,
}
const btn = (color='#1E90FF') => ({
  padding:'8px 18px', borderRadius:7, border:'none', cursor:'pointer',
  fontFamily:T.font, fontWeight:700, fontSize:11, letterSpacing:'.06em',
  textTransform:'uppercase', color:'#fff', background:color,
  transition:'opacity .15s',
})
const btnSm = (color='#E8631A') => ({
  ...btn(color), padding:'6px 14px', fontSize:10,
})

// ── Timeline component ────────────────────────────────────────────────────────

function Timeline({ history }) {
  if (!history || history.length === 0) return null
  return (
    <div style={{ marginTop:16 }}>
      <div style={{ fontSize:10, fontWeight:700, color:T.txtMuted, letterSpacing:'.08em',
        textTransform:'uppercase', marginBottom:8 }}>Case History</div>
      <div style={{ position:'relative', paddingLeft:20 }}>
        {/* vertical line */}
        <div style={{ position:'absolute', left:6, top:8, bottom:8,
          width:1, background:T.border }} />
        {history.map((h, i) => {
          const sm = STATUS_META[h.to_status] || {}
          return (
            <div key={h.id || i} style={{ position:'relative', marginBottom:12 }}>
              <div style={{ position:'absolute', left:-14, top:4, width:8, height:8,
                borderRadius:'50%', background:sm.color || T.txtMuted,
                border:`2px solid ${T.surface}` }} />
              <div style={{ fontSize:11, fontWeight:700, color:sm.color || T.txtMuted }}>
                {sm.label || h.to_status}
              </div>
              <div style={{ fontSize:10, color:T.txtSecondary }}>
                {fmtDT(h.created_at)}
                {h.actor_name && <span style={{ color:T.txtMuted }}> · {h.actor_name}</span>}
                {h.actor_role && <span style={{ color:T.txtMuted, fontStyle:'italic' }}> ({h.actor_role})</span>}
              </div>
              {h.note && (
                <div style={{ fontSize:10, color:T.txtSecondary, marginTop:2,
                  fontStyle:'italic', paddingLeft:4, borderLeft:`2px solid ${T.border}` }}>
                  {h.note}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function ComplianceReview({ session, roleCtx }) {
  const role      = roleCtx?.role || ROLES.GUEST
  const userName  = session?.user?.user_metadata?.full_name || session?.user?.email || ''
  const isCO      = role === ROLES.COMPLIANCE_OFFICER
  const isAdmin   = role === ROLES.ADMIN
  const canOpen   = can(role, 'open_infraction_case')
  const canResolve= can(role, 'log_co_resolution')
  const canApprove= can(role, 'approve_resolution')
  const canPayment= can(role, 'mark_penalty_paid')

  const [infractions,   setInfractions]   = useState([])
  const [resolutions,   setResolutions]   = useState({})   // keyed by infraction_id
  const [history,       setHistory]       = useState({})   // keyed by infraction_id
  const [loading,       setLoading]       = useState(true)
  const [expanded,      setExpanded]      = useState(null)
  const [saving,        setSaving]        = useState({})
  const [msg,           setMsg]           = useState(null)

  // Forms
  const [openForm,      setOpenForm]      = useState({})   // {infraction_id: {meeting_date}}
  const [resForm,       setResForm]       = useState({})   // {infraction_id: {type, amount, notes}}
  const [adminForm,     setAdminForm]     = useState({})   // {infraction_id: {decision, notes, modified_to}}

  // Filters
  const [filterStatus,  setFilterStatus]  = useState('')

  const load = useCallback(async () => {
    const [{ data: inf }, { data: res }, { data: hist }] = await Promise.all([
      supabase
        .from('infractions')
        .select('*, super_agents(company_name, owner_name, lgas(name, tier))')
        .order('date_logged', { ascending: false })
        .limit(400),
      supabase
        .from('infraction_resolutions')
        .select('*')
        .order('created_at', { ascending: false }),
      supabase
        .from('infraction_status_history')
        .select('*')
        .order('created_at', { ascending: true }),
    ])
    setInfractions(inf || [])
    // Index resolutions and history by infraction_id
    const resMap = {}
    ;(res || []).forEach(r => { resMap[r.infraction_id] = r })
    const histMap = {}
    ;(hist || []).forEach(h => {
      if (!histMap[h.infraction_id]) histMap[h.infraction_id] = []
      histMap[h.infraction_id].push(h)
    })
    setResolutions(resMap)
    setHistory(histMap)
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  // ── Helper: write a history row + update infraction status ────────────────
  async function transition(infractionId, toStatus, note, actorRole) {
    const fromStatus = infractions.find(i => i.id === infractionId)?.resolution_status || 'reported'
    await Promise.all([
      supabase.from('infraction_status_history').insert([{
        infraction_id: infractionId,
        from_status:   fromStatus,
        to_status:     toStatus,
        actor_name:    userName,
        actor_role:    actorRole || role,
        note,
      }]),
      supabase.from('infractions')
        .update({ resolution_status: toStatus })
        .eq('id', infractionId),
    ])
  }

  // ── Stage 2: CO opens a case ───────────────────────────────────────────────
  async function openCase(inf) {
    if (!canOpen) return
    const f = openForm[inf.id] || {}
    if (!f.meeting_date) { setMsg({ type:'err', text:'Please enter the PMO meeting date.' }); return }
    setSaving(s => ({ ...s, [inf.id]: true }))
    setMsg(null)
    const now = new Date().toISOString()

    // Upsert resolution row (opened stage)
    const { error } = await supabase.from('infraction_resolutions').upsert([{
      infraction_id: inf.id,
      opened_by:     userName,
      opened_at:     now,
      meeting_date:  f.meeting_date,
    }], { onConflict: 'infraction_id' })

    if (error) { setMsg({ type:'err', text: error.message }); setSaving(s => ({ ...s, [inf.id]: false })); return }

    await transition(inf.id, 'under_review', `Case opened for PMO review · Meeting: ${f.meeting_date}`)
    setOpenForm(of => { const n = { ...of }; delete n[inf.id]; return n })
    setSaving(s => ({ ...s, [inf.id]: false }))
    load()
  }

  // ── Stage 3: CO logs PMO resolution ───────────────────────────────────────
  async function logResolution(inf) {
    if (!canResolve) return
    const f = resForm[inf.id] || {}
    if (!f.type) { setMsg({ type:'err', text:'Select a resolution type.' }); return }
    if (f.type === 'penalty' && (!f.amount || isNaN(Number(f.amount)))) {
      setMsg({ type:'err', text:'Enter a valid penalty amount.' }); return
    }
    setSaving(s => ({ ...s, [inf.id]: true }))
    setMsg(null)
    const now = new Date().toISOString()

    const { error } = await supabase.from('infraction_resolutions').upsert([{
      infraction_id:   inf.id,
      resolution_type: f.type,
      penalty_amount:  f.type === 'penalty' ? Number(f.amount) : null,
      co_notes:        f.notes || null,
      resolved_by:     userName,
      resolved_at:     now,
    }], { onConflict: 'infraction_id' })

    if (error) { setMsg({ type:'err', text: error.message }); setSaving(s => ({ ...s, [inf.id]: false })); return }

    await transition(inf.id, 'pending_admin',
      `PMO resolution logged: ${f.type}${f.type === 'penalty' ? ` — ₦${Number(f.amount).toLocaleString()}` : ''}`)

    // Clear form
    setResForm(rf => { const n = { ...rf }; delete n[inf.id]; return n })
    setSaving(s => ({ ...s, [inf.id]: false }))
    load()
  }

  // ── Stage 4: Admin approves / modifies ────────────────────────────────────
  async function adminApprove(inf) {
    if (!canApprove) return
    const f = adminForm[inf.id] || {}
    const res = resolutions[inf.id]
    if (!res) return
    setSaving(s => ({ ...s, [inf.id + '_admin']: true }))
    setMsg(null)
    const now = new Date().toISOString()

    const finalType = f.modified_to || res.resolution_type

    // Determine next status
    let nextStatus = 'closed'
    if (finalType === 'penalty') nextStatus = 'pending_payment'

    const { error } = await supabase.from('infraction_resolutions').upsert([{
      infraction_id:     inf.id,
      admin_decision:    f.modified_to ? 'modified' : 'approved',
      admin_notes:       f.notes || null,
      admin_modified_to: f.modified_to || null,
      reviewed_by:       userName,
      reviewed_at:       now,
      closed_at:         nextStatus === 'closed' ? now : null,
      final_status:      nextStatus === 'closed' ? finalType : null,
    }], { onConflict: 'infraction_id' })

    if (error) { setMsg({ type:'err', text: error.message }); setSaving(s => ({ ...s, [inf.id + '_admin']: false })); return }

    const note = f.modified_to
      ? `Admin modified resolution: ${res.resolution_type} → ${f.modified_to}. ${f.notes || ''}`
      : `Admin approved: ${finalType}. ${f.notes || ''}`
    await transition(inf.id, nextStatus, note.trim(), 'admin')

    setAdminForm(af => { const n = { ...af }; delete n[inf.id]; return n })
    setSaving(s => ({ ...s, [inf.id + '_admin']: false }))
    load()
  }

  // ── Stage 5: CO marks penalty paid ───────────────────────────────────────
  async function markPaid(inf) {
    if (!canPayment) return
    setSaving(s => ({ ...s, [inf.id + '_pay']: true }))
    setMsg(null)
    const now = new Date().toISOString()
    const res = resolutions[inf.id]

    const { error } = await supabase.from('infraction_resolutions').upsert([{
      infraction_id:           inf.id,
      penalty_paid:            true,
      payment_confirmed_by:    userName,
      payment_confirmed_at:    now,
      closed_at:               now,
      final_status:            res?.admin_modified_to || res?.resolution_type || 'penalty',
    }], { onConflict: 'infraction_id' })

    if (error) { setMsg({ type:'err', text: error.message }); setSaving(s => ({ ...s, [inf.id + '_pay']: false })); return }

    await transition(inf.id, 'closed', 'Penalty payment confirmed', role)
    setSaving(s => ({ ...s, [inf.id + '_pay']: false }))
    load()
  }

  // ── Filtered list ─────────────────────────────────────────────────────────
  const filtered = infractions.filter(i => {
    if (filterStatus && i.resolution_status !== filterStatus) return false
    return true
  })

  const selS = {
    fontSize:12, fontFamily:T.font, background:T.surface3,
    border:`1px solid ${T.border}`, borderRadius:6, padding:'7px 12px',
    color:T.txtPrimary, cursor:'pointer',
  }

  if (loading) return <div style={{ color:T.txtSecondary, fontSize:13 }}>Loading…</div>

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:20, fontWeight:800, color:T.txtPrimary, marginBottom:4 }}>
          Compliance Review
        </div>
        <div style={{ fontSize:12, color:T.txtSecondary }}>
          PMO case management · Resolution tracking · Admin approval
        </div>
      </div>

      {msg && (
        <div style={{ padding:'10px 14px', borderRadius:8, marginBottom:16, fontSize:12,
          background: msg.type === 'err' ? 'rgba(248,81,73,.12)' : 'rgba(63,185,80,.12)',
          color:       msg.type === 'err' ? '#f85149'            : '#3fb950',
          border:`1px solid ${msg.type === 'err' ? 'rgba(248,81,73,.3)' : 'rgba(63,185,80,.3)'}` }}>
          {msg.text}
        </div>
      )}

      {/* Stats row */}
      <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        {Object.entries(STATUS_META).map(([key, m]) => {
          const count = infractions.filter(i => i.resolution_status === key).length
          return (
            <div key={key}
              onClick={() => setFilterStatus(prev => prev === key ? '' : key)}
              style={{ ...card, padding:'12px 18px', cursor:'pointer', minWidth:100, flex:1,
                border: filterStatus === key ? `1px solid ${m.color}` : `1px solid ${T.border}`,
                background: filterStatus === key ? m.bg : T.surface }}>
              <div style={{ fontSize:20, fontWeight:800, color:m.color }}>{count}</div>
              <div style={{ fontSize:10, fontWeight:600, color:T.txtMuted, textTransform:'uppercase',
                letterSpacing:'.06em', marginTop:2 }}>{m.label}</div>
            </div>
          )
        })}
      </div>

      {/* Filter bar */}
      <div style={{ display:'flex', gap:10, marginBottom:16, alignItems:'center', flexWrap:'wrap' }}>
        <select style={selS} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {Object.entries(STATUS_META).map(([k, m]) => (
            <option key={k} value={k}>{m.label}</option>
          ))}
        </select>
        {filterStatus && (
          <button style={btnSm('#444')} onClick={() => setFilterStatus('')}>Clear</button>
        )}
        <div style={{ marginLeft:'auto', fontSize:11, color:T.txtMuted }}>
          {filtered.length} infraction{filtered.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Case list */}
      <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
        {filtered.length === 0 && (
          <div style={{ ...card, color:T.txtMuted, fontSize:13, textAlign:'center', padding:32 }}>
            No infractions match the current filter.
          </div>
        )}

        {filtered.map(inf => {
          const status  = inf.resolution_status || 'reported'
          const res     = resolutions[inf.id]
          const hist    = history[inf.id] || []
          const isOpen  = expanded === inf.id
          const finalType = res?.admin_modified_to || res?.resolution_type

          return (
            <div key={inf.id} style={{ ...card, padding:0, overflow:'hidden' }}>

              {/* Row header — always visible */}
              <div
                onClick={() => setExpanded(isOpen ? null : inf.id)}
                style={{ display:'flex', alignItems:'center', gap:12, padding:'14px 18px',
                  cursor:'pointer', userSelect:'none',
                  borderBottom: isOpen ? `1px solid ${T.border}` : 'none' }}>

                <StatusBadge status={status} />

                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:13, color:T.txtPrimary, whiteSpace:'nowrap',
                    overflow:'hidden', textOverflow:'ellipsis' }}>
                    {inf.super_agents?.company_name || '—'}
                  </div>
                  <div style={{ fontSize:10, color:T.txtMuted }}>
                    {inf.super_agents?.owner_name} · {inf.super_agents?.lgas?.name} · {fmtDate(inf.date_logged)}
                  </div>
                </div>

                {res?.resolution_type && (
                  <ResBadge type={finalType || res.resolution_type} />
                )}
                {res?.penalty_amount && (
                  <div style={{ fontSize:11, fontWeight:700, color:'#f0883e' }}>
                    {fmtNaira(res.penalty_amount)}
                  </div>
                )}

                <div style={{ fontSize:18, color:T.txtMuted, lineHeight:1 }}>
                  {isOpen ? '▲' : '▼'}
                </div>
              </div>

              {/* Expanded detail */}
              {isOpen && (
                <div style={{ padding:'18px 20px', display:'flex', gap:24, flexWrap:'wrap' }}>

                  {/* Left: infraction details + history */}
                  <div style={{ flex:'1 1 280px', minWidth:0 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:T.txtMuted, textTransform:'uppercase',
                      letterSpacing:'.07em', marginBottom:10 }}>Infraction Details</div>

                    <div style={{ display:'grid', gridTemplateColumns:'auto 1fr', gap:'5px 14px',
                      fontSize:12, marginBottom:12 }}>
                      <span style={{ color:T.txtMuted }}>Date</span>
                      <span style={{ color:T.txtPrimary }}>{fmtDate(inf.date_logged)}</span>
                      <span style={{ color:T.txtMuted }}>Logged by</span>
                      <span style={{ color:T.txtPrimary }}>{inf.logged_by || '—'}</span>
                      <span style={{ color:T.txtMuted }}>Level</span>
                      <span style={{ color:'#f0883e', fontWeight:600 }}>{inf.enforcement_level || '—'}</span>
                      <span style={{ color:T.txtMuted }}>Types</span>
                      <span style={{ color:T.txtPrimary }}>
                        {(inf.infraction_types || []).join(', ') || '—'}
                      </span>
                    </div>

                    {inf.description && (
                      <div style={{ fontSize:11, color:T.txtSecondary, marginBottom:8,
                        padding:'8px 12px', background:T.surface2, borderRadius:6,
                        borderLeft:`3px solid ${T.border}` }}>
                        {inf.description}
                      </div>
                    )}

                    {/* Resolution summary (if exists) */}
                    {res && (
                      <div style={{ marginTop:12 }}>
                        <div style={{ fontSize:11, fontWeight:700, color:T.txtMuted, textTransform:'uppercase',
                          letterSpacing:'.07em', marginBottom:8 }}>Resolution</div>
                        <div style={{ display:'grid', gridTemplateColumns:'auto 1fr', gap:'5px 14px', fontSize:12 }}>
                          {res.meeting_date && <>
                            <span style={{ color:T.txtMuted }}>Meeting date</span>
                            <span style={{ fontWeight:600, color:T.txtPrimary }}>{fmtDate(res.meeting_date)}</span>
                          </>}
                          {res.opened_at && <>
                            <span style={{ color:T.txtMuted }}>Opened</span>
                            <span>{fmtDT(res.opened_at)} · {res.opened_by}</span>
                          </>}
                          {res.resolution_type && <>
                            <span style={{ color:T.txtMuted }}>Decision</span>
                            <span><ResBadge type={res.resolution_type} /></span>
                          </>}
                          {res.admin_modified_to && <>
                            <span style={{ color:T.txtMuted }}>Admin modified</span>
                            <span><ResBadge type={res.admin_modified_to} /></span>
                          </>}
                          {res.penalty_amount && <>
                            <span style={{ color:T.txtMuted }}>Penalty</span>
                            <span style={{ color:'#f0883e', fontWeight:700 }}>{fmtNaira(res.penalty_amount)}</span>
                          </>}
                          {res.penalty_paid && <>
                            <span style={{ color:T.txtMuted }}>Payment</span>
                            <span style={{ color:'#3fb950', fontWeight:700 }}>Paid ✓</span>
                          </>}
                          {res.co_notes && <>
                            <span style={{ color:T.txtMuted }}>CO notes</span>
                            <span style={{ fontStyle:'italic' }}>{res.co_notes}</span>
                          </>}
                          {res.admin_notes && <>
                            <span style={{ color:T.txtMuted }}>Admin note</span>
                            <span style={{ fontStyle:'italic' }}>{res.admin_notes}</span>
                          </>}
                        </div>
                      </div>
                    )}

                    {/* Timeline */}
                    <Timeline history={hist} />
                  </div>

                  {/* Right: action panel */}
                  <div style={{ flex:'0 0 260px', minWidth:220 }}>

                    {/* ── Stage 2: CO opens case ── */}
                    {status === 'reported' && canOpen && (
                      <div style={{ padding:'14px 16px', background:T.surface2, borderRadius:10,
                        border:`1px solid ${T.border}`, marginBottom:12 }}>
                        <div style={{ fontSize:11, fontWeight:700, color:T.txtMuted, textTransform:'uppercase',
                          letterSpacing:'.07em', marginBottom:10 }}>Open Case</div>
                        <p style={{ fontSize:11, color:T.txtSecondary, marginBottom:12, lineHeight:1.5 }}>
                          Opening this case marks it as under review and logs you as the Compliance Officer.
                        </p>
                        <label style={fieldLabel}>PMO Meeting Date *</label>
                        <input
                          type="date"
                          style={{ ...inp, marginBottom:12 }}
                          value={(openForm[inf.id] || {}).meeting_date || ''}
                          onChange={e => setOpenForm(of => ({ ...of, [inf.id]: { ...of[inf.id], meeting_date: e.target.value } }))} />
                        <button
                          style={btn('#E8631A')}
                          disabled={saving[inf.id]}
                          onClick={() => openCase(inf)}>
                          {saving[inf.id] ? 'Opening…' : 'Open Case →'}
                        </button>
                      </div>
                    )}

                    {/* ── Stage 3: CO logs PMO resolution ── */}
                    {status === 'under_review' && canResolve && (
                      <div style={{ padding:'14px 16px', background:T.surface2, borderRadius:10,
                        border:`1px solid ${T.border}`, marginBottom:12 }}>
                        <div style={{ fontSize:11, fontWeight:700, color:T.txtMuted, textTransform:'uppercase',
                          letterSpacing:'.07em', marginBottom:10 }}>Log PMO Resolution</div>

                        <label style={fieldLabel}>Resolution Type *</label>
                        <select
                          style={{ ...inp, marginBottom:0 }}
                          value={(resForm[inf.id] || {}).type || ''}
                          onChange={e => setResForm(rf => ({ ...rf, [inf.id]: { ...rf[inf.id], type: e.target.value } }))}>
                          <option value="">— Select —</option>
                          <option value="warning">Warning</option>
                          <option value="penalty">Penalty</option>
                          <option value="suspension">Suspension</option>
                          <option value="termination">Termination</option>
                        </select>

                        {(resForm[inf.id] || {}).type === 'penalty' && (
                          <>
                            <label style={fieldLabel}>Penalty Amount (₦) *</label>
                            <input
                              style={inp}
                              type="number" min="0" placeholder="e.g. 50000"
                              value={(resForm[inf.id] || {}).amount || ''}
                              onChange={e => setResForm(rf => ({ ...rf, [inf.id]: { ...rf[inf.id], amount: e.target.value } }))} />
                          </>
                        )}

                        <label style={fieldLabel}>Meeting Notes</label>
                        <textarea
                          style={{ ...inp, height:70, resize:'vertical' }}
                          placeholder="Key outcomes, conditions, attendees…"
                          value={(resForm[inf.id] || {}).notes || ''}
                          onChange={e => setResForm(rf => ({ ...rf, [inf.id]: { ...rf[inf.id], notes: e.target.value } }))} />

                        <button
                          style={{ ...btn('#1E90FF'), marginTop:10, width:'100%' }}
                          disabled={saving[inf.id]}
                          onClick={() => logResolution(inf)}>
                          {saving[inf.id] ? 'Saving…' : 'Submit for Admin Approval'}
                        </button>
                      </div>
                    )}

                    {/* ── Stage 4: Admin approval ── */}
                    {status === 'pending_admin' && canApprove && res && (
                      <div style={{ padding:'14px 16px', background:T.surface2, borderRadius:10,
                        border:`1px solid rgba(30,144,255,.3)`, marginBottom:12 }}>
                        <div style={{ fontSize:11, fontWeight:700, color:'#1E90FF', textTransform:'uppercase',
                          letterSpacing:'.07em', marginBottom:10 }}>Admin Review</div>

                        <div style={{ fontSize:12, color:T.txtSecondary, marginBottom:10 }}>
                          CO Resolution: <strong style={{ color:T.txtPrimary }}>
                            {res.resolution_type}{res.penalty_amount ? ` · ${fmtNaira(res.penalty_amount)}` : ''}
                          </strong>
                        </div>

                        <label style={fieldLabel}>Modify Resolution (optional)</label>
                        <select
                          style={{ ...inp, marginBottom:0 }}
                          value={(adminForm[inf.id] || {}).modified_to || ''}
                          onChange={e => setAdminForm(af => ({ ...af, [inf.id]: { ...af[inf.id], modified_to: e.target.value } }))}>
                          <option value="">— Approve as-is —</option>
                          <option value="warning">Change to: Warning</option>
                          <option value="penalty">Change to: Penalty</option>
                          <option value="suspension">Change to: Suspension</option>
                          <option value="termination">Change to: Termination</option>
                        </select>

                        <label style={fieldLabel}>Admin Comment</label>
                        <textarea
                          style={{ ...inp, height:70, resize:'vertical' }}
                          placeholder="Final decision rationale…"
                          value={(adminForm[inf.id] || {}).notes || ''}
                          onChange={e => setAdminForm(af => ({ ...af, [inf.id]: { ...af[inf.id], notes: e.target.value } }))} />

                        <button
                          style={{ ...btn('#3fb950'), marginTop:10, width:'100%' }}
                          disabled={saving[inf.id + '_admin']}
                          onClick={() => adminApprove(inf)}>
                          {saving[inf.id + '_admin'] ? 'Saving…' : (adminForm[inf.id]?.modified_to ? 'Modify & Approve' : 'Approve Decision')}
                        </button>
                      </div>
                    )}

                    {/* ── Stage 5: Mark penalty paid ── */}
                    {status === 'pending_payment' && canPayment && (
                      <div style={{ padding:'14px 16px', background:T.surface2, borderRadius:10,
                        border:`1px solid rgba(240,136,62,.3)`, marginBottom:12 }}>
                        <div style={{ fontSize:11, fontWeight:700, color:'#f0883e', textTransform:'uppercase',
                          letterSpacing:'.07em', marginBottom:8 }}>Awaiting Payment</div>
                        <div style={{ fontSize:12, color:T.txtSecondary, marginBottom:12 }}>
                          Penalty: <strong style={{ color:'#f0883e' }}>{fmtNaira(res?.penalty_amount)}</strong>
                        </div>
                        <button
                          style={btn('#3fb950')}
                          disabled={saving[inf.id + '_pay']}
                          onClick={() => markPaid(inf)}>
                          {saving[inf.id + '_pay'] ? 'Confirming…' : '✓ Mark Penalty Paid'}
                        </button>
                      </div>
                    )}

                    {/* Closed state */}
                    {status === 'closed' && (
                      <div style={{ padding:'14px 16px', background:'rgba(63,185,80,.06)', borderRadius:10,
                        border:'1px solid rgba(63,185,80,.25)' }}>
                        <div style={{ fontSize:11, fontWeight:700, color:'#3fb950', textTransform:'uppercase',
                          letterSpacing:'.07em', marginBottom:6 }}>Closed</div>
                        <div style={{ fontSize:11, color:T.txtSecondary }}>
                          Final: <strong style={{ color:T.txtPrimary }}>{finalType || '—'}</strong>
                        </div>
                        {res?.closed_at && (
                          <div style={{ fontSize:10, color:T.txtMuted, marginTop:4 }}>
                            {fmtDT(res.closed_at)}
                          </div>
                        )}
                      </div>
                    )}

                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
